import { useContext, useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Context } from '../../../AuthContext';

const GOOGLE_CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.calendarlist.readonly';

/**
 * Read-only Google Calendar iframe driven by the user's saved prefs from the
 * My Calendar tab. No sidebar, no add-calendar UI — just the embed.
 *
 *  - Not connected (or no calendar scope) → "Connect your Google account" CTA.
 *  - Connected but no calendars toggled visible → small "set it up" hint.
 *  - Otherwise → iframe with the saved visible calendars.
 */
const EmbeddedCalendar = () => {
  const { user } = useContext(Context);
  const [phase, setPhase] = useState('loading'); // loading | connect | empty | ready | error
  const [embedUrl, setEmbedUrl] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!user) return;
      try {
        const token = await user.getIdToken();
        const headers = { Authorization: `Bearer ${token}` };

        const statusRes = await fetch(
          `${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/status`,
          { headers }
        );
        if (!statusRes.ok) throw new Error(`status_${statusRes.status}`);
        const status = await statusRes.json();
        if (cancelled) return;

        const hasCalendar =
          status.connected && (status.scopes || []).includes(GOOGLE_CALENDAR_SCOPE);
        if (!hasCalendar) {
          setPhase('connect');
          return;
        }

        // Need the calendar list to map ids → backgroundColor and to drop
        // any visible ids that the user no longer has access to in Google.
        const [calRes, prefsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/calendars`, { headers }),
          fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/calendar-prefs`, { headers }),
        ]);
        if (calRes.status === 401) {
          if (!cancelled) setPhase('connect');
          return;
        }
        if (!calRes.ok) throw new Error(`calendars_${calRes.status}`);
        const calData = await calRes.json();
        const prefs = prefsRes.ok ? await prefsRes.json() : { visible: [] };
        if (cancelled) return;

        const calendars = calData.calendars || [];
        const calendarById = new Map(calendars.map((c) => [c.id, c]));
        const visible = (prefs.visible || []).filter((id) => calendarById.has(id));

        if (visible.length === 0) {
          setPhase('empty');
          return;
        }

        const params = new URLSearchParams();
        params.append('ctz', Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
        params.append('mode', 'MONTH');
        params.append('wkst', '1');
        params.append('showTitle', '0');
        params.append('showPrint', '0');
        const ordered = [
          ...visible.filter((id) => calendarById.get(id)?.primary),
          ...visible.filter((id) => !calendarById.get(id)?.primary),
        ];
        ordered.forEach((id) => {
          params.append('src', id);
          const color = calendarById.get(id)?.backgroundColor;
          if (color) params.append('color', color);
        });
        setEmbedUrl(`https://calendar.google.com/calendar/embed?${params.toString()}`);
        setPhase('ready');
      } catch (_) {
        if (!cancelled) setPhase('error');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const wrap = (children, extra = {}) => (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 16,
        ...extra,
      }}
    >
      {children}
    </div>
  );

  if (phase === 'loading') return wrap(<CircularProgress size={24} />);

  if (phase === 'connect') {
    return wrap(
      <div>
        <img
          src="/workbench/google.svg"
          alt="Google"
          style={{ width: 40, height: 40, marginBottom: 12 }}
        />
        <h4 style={{ margin: 0, marginBottom: 6 }}>Connect your Google account</h4>
        <p style={{ margin: 0, marginBottom: 12, color: '#475467', fontSize: 13 }}>
          Link your Google account to view your calendar here.
        </p>
        <RouterLink
          to="/profile/connected-accounts"
          style={{
            background: '#356AC3',
            color: '#fff',
            padding: '8px 16px',
            borderRadius: 6,
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          Go to Connected Accounts
        </RouterLink>
      </div>
    );
  }

  if (phase === 'empty') {
    return wrap(
      <p style={{ margin: 0, color: '#667085', fontSize: 13 }}>
        Set up your calendars from the <strong>My Calendar</strong> tab to see them here.
      </p>
    );
  }

  if (phase === 'error') {
    return wrap(
      <p style={{ margin: 0, color: '#B42318', fontSize: 13 }}>
        Could not load your calendar. Please try again later.
      </p>
    );
  }

  return (
    <iframe
      title="My Google Calendar"
      src={embedUrl}
      style={{ border: 0, width: '100%', height: '100%' }}
      frameBorder="0"
      scrolling="no"
    />
  );
};

export default EmbeddedCalendar;
